import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface GroupedDeviceTileProps {
  groupName: string;
  groupColor?: string | null;
  deviceCount: number;
  thumbnailUrl?: string | null;
  mostRecentUpdate?: string | null;
  onClick?: () => void;
}

export const GroupedDeviceTile = ({
  groupName,
  groupColor,
  deviceCount,
  thumbnailUrl,
  mostRecentUpdate,
  onClick,
}: GroupedDeviceTileProps) => {
  return (
    <Card
      className="brand-device-card group relative cursor-pointer hover:shadow-lg transition-all duration-200 overflow-hidden"
      onClick={onClick}
    >
      {/* Stacked cards effect */}
      <div className="absolute -bottom-1 -right-1 w-full h-full bg-card border rounded-lg shadow-sm opacity-60 transform translate-x-1 translate-y-1 -z-10" />
      <div className="absolute -bottom-2 -right-2 w-full h-full bg-card border rounded-lg shadow-sm opacity-30 transform translate-x-2 translate-y-2 -z-20" />

      {/* Main card content */}
      <div className="relative aspect-video overflow-hidden bg-muted">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={groupName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Camera className="h-12 w-12 text-muted-foreground/40" />
          </div>
        )}

        {/* Device count badge */}
        <Badge
          className="absolute top-2 right-2 bg-background/90 text-foreground hover:bg-background/90"
          variant="secondary"
        >
          <Camera className="h-3 w-3 mr-1" />
          {deviceCount} device{deviceCount !== 1 ? "s" : ""}
        </Badge>

        {/* Group color indicator */}
        {groupColor && (
          <div
            className="absolute top-2 left-2 w-4 h-4 rounded-full border-2 border-white shadow-sm"
            style={{ backgroundColor: groupColor }}
          />
        )}
      </div>

      <CardContent className="p-3">
        <h3 className="font-semibold text-sm truncate">{groupName}</h3>
        {mostRecentUpdate && (
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              Updated {formatDistanceToNow(new Date(mostRecentUpdate), { addSuffix: true })}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
